'use client'
import React, { FC, useState } from 'react'
import { IHomePageTemplateCtaBottomBlock } from '@/types/firebase'
import Image from 'next/image'
import BottomCta from '@/images/home/bottomcta.png'
import {
  GlobeIcon,
  PiIcon,
  MathIcon,
  ChemicalIcon,
  CubeIcon,
} from '@/components/icons/index'
import styles from './styles/index.module.css'
import Link from 'next/link'

interface Props {
  customContent: IHomePageTemplateCtaBottomBlock
}

export const CommonCtaBottomBlock: FC<Props> = ({
  customContent: { title, subtitle, statistics, link },
}) => {
  const [hoveredElementId, setHoveredElementId] = useState<string>()

  return (
    <div className="flex rounded-[24px] w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        className={`relative bg-v8-blue rounded-[24px] shadow-v8-card w-full`}
      >
        {/* Student Image */}
        <div className="absolute z-11 bottom-0 left-[-87px] hidden lg:flex">
          <Image src={BottomCta} width={517} height={418} alt="Cta Bottom" />
        </div>

        <div className={`relative w-full overflow-hidden ${styles.container}`}>
          {/* Gray Circle BG SVG */}
          <div className="absolute top-28 left-0 overflow-hidden z-10">
            <svg
              width="600"
              height="810"
              viewBox="0 0 600 810"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                opacity="0.1"
                cx="100"
                cy="405"
                r="355"
                stroke="white"
                strokeWidth="100"
              />
            </svg>
          </div>

          {/* Content */}
          <div className="flex flex-col justify-center z-20 gap-8 xl:pr-10">
            <div className="flex flex-col gap-3 max-md:items-center">
              {/* Title */}
              <h2
                className="md:text-nowrap max-md:text-center max-md:max-w-[360px]"
                id="title"
                style={{
                  fontSize: `${title.styles.fontSize}px`,
                  fontWeight: +title.styles.fontWeight,
                  lineHeight: `${title.styles.lineHeight}px`,
                  fontStyle: title.styles.isItalic ? 'italic' : 'normal',
                  color:
                    hoveredElementId === 'title'
                      ? title.styles.onHoverColor
                      : title.styles.color,
                }}
                onMouseEnter={() => setHoveredElementId('title')}
                onMouseLeave={() => setHoveredElementId(undefined)}
              >
                {title.text}
              </h2>
              {/* Subtitle */}
              <p
                className="max-md:text-center"
                id="subtitle"
                style={{
                  fontSize: `${subtitle.styles.fontSize}px`,
                  fontWeight: +subtitle.styles.fontWeight,
                  lineHeight: `${subtitle.styles.lineHeight}px`,
                  fontStyle: subtitle.styles.isItalic ? 'italic' : 'normal',
                  color:
                    hoveredElementId === 'subtitle'
                      ? subtitle.styles.onHoverColor
                      : subtitle.styles.color,
                }}
                onMouseEnter={() => setHoveredElementId('subtitle')}
                onMouseLeave={() => setHoveredElementId(undefined)}
              >
                {subtitle.text}
              </p>
            </div>

            <div className="flex flex-col justify-center lg:pl-20 xl:pl-10 md:w-fit">
              <ul className="flex flex-col items-center md:items-start gap-4 mb-8">
                {statistics.map((stat, i) => {
                  return (
                    <p
                      key={`${i}_${stat.category.text}-${stat.value.text}`}
                      className="text-center"
                      id={`${i}_${stat.category.text}`}
                      style={{
                        fontSize: `${stat.category.styles.fontSize}px`,
                        fontWeight: +stat.category.styles.fontWeight,
                        lineHeight: `${stat.category.styles.lineHeight}px`,
                        fontStyle: stat.category.styles.isItalic
                          ? 'italic'
                          : 'normal',
                        color:
                          hoveredElementId === `${i}_${stat.category.text}`
                            ? stat.category.styles.onHoverColor
                            : stat.category.styles.color,
                      }}
                      onMouseEnter={() =>
                        setHoveredElementId(`${i}_${stat.category.text}`)
                      }
                      onMouseLeave={() => setHoveredElementId(undefined)}
                    >
                      <span
                        className=""
                        id={`${i}_${stat.value.text}`}
                        style={{
                          fontSize: `${stat.value.styles.fontSize}px`,
                          fontWeight: +stat.value.styles.fontWeight,
                          lineHeight: `${stat.value.styles.lineHeight}px`,
                          fontStyle: stat.value.styles.isItalic
                            ? 'italic'
                            : 'normal',
                          color:
                            hoveredElementId === `${i}_${stat.value.text}`
                              ? stat.value.styles.onHoverColor
                              : stat.value.styles.color,
                        }}
                        onMouseEnter={() =>
                          setHoveredElementId(`${i}_${stat.value.text}`)
                        }
                        onMouseLeave={() => setHoveredElementId(undefined)}
                      >
                        {stat.value.text + ' '}
                      </span>
                      {stat.category.text}
                    </p>
                  )
                })}
              </ul>

              <div className="w-fit self-center">
                <Link
                  id="link"
                  href={link.href}
                  target={link.openInNewWindow ? '_blank' : undefined}
                  className="block duration-300 rounded-md border px-4 py-3 duration-300 text-center text-nowrap"
                  style={{
                    fontSize: `${link.styles.fontSize}px`,
                    fontWeight: +link.styles.fontWeight,
                    lineHeight: `${link.styles.lineHeight}px`,
                    fontStyle: link.styles.isItalic ? 'italic' : 'normal',
                    color:
                      hoveredElementId === 'link'
                        ? link.styles.onHoverColor
                        : link.styles.color,
                    backgroundColor:
                      hoveredElementId === 'link'
                        ? link.styles.onHoverBackgroundColor
                        : link.styles.backgroundColor,

                    borderColor:
                      hoveredElementId === 'link'
                        ? link.styles.onHoverBorderColor || undefined
                        : link.styles.borderColor || undefined,
                  }}
                  onMouseEnter={() => setHoveredElementId('link')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {link.text}
                </Link>
              </div>
            </div>
          </div>

          {/* SVG`s*/}
          <div
            className={`flex justify-center items-center space-x-4 ${styles.globePiIconWrapper}`}
          >
            <PiIcon className="w-6 h-6" />
            <GlobeIcon className="w-14 h-14" />
          </div>

          <div className={`w-28 h-28 ${styles.mathIconWrapper}`}>
            <MathIcon className="w-28 h-28" />
          </div>

          <div className={`flex space-x-10 ${styles.cubeChemIconWrapper}`}>
            <ChemicalIcon className="w-24 h-24" />
            <CubeIcon className="w-24 h-24" />
          </div>
        </div>
      </div>
    </div>
  )
}
